import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  Grid,
  Chip,
  Toolbar,
  Button,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  QrCode2,
  ContentCopy,
  Download,
  Delete,
  DriveFileMove,
  Folder as FolderIcon,
  DeleteSweep,
  DriveFileMoveOutlined,
  CheckBoxOutlineBlank,
  LibraryAddCheck,
  Edit,
  Info,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useFiles, useDeleteFile } from '../../hooks/useFiles';
import { useQueryClient } from '@tanstack/react-query';
import FileGrid from './FileGrid';
import FileTable from './FileTable';
import FilePreviewDialog from './FilePreviewDialog';
import FileDetailPanel from './FileDetailPanel';
import EmptyState from '../common/EmptyState';
import ConfirmDialog from '../common/ConfirmDialog';
import MoveFileDialog from './MoveFileDialog';
import ExportLinksDialog from '../common/ExportLinksDialog';
import api, { filesAPI } from '../../services/api';

function FolderCards({ subfolders, onFolderSelect }) {
  if (!subfolders || subfolders.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Cartelle
      </Typography>
      <Grid container spacing={1.5}>
        {subfolders.map((folder) => (
          <Grid item xs={4} sm={4} md={3} lg={2} key={folder.id}>
            <Paper
              elevation={0}
              onClick={() => onFolderSelect(folder.id)}
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.light',
                },
                transition: 'all 0.15s',
              }}
            >
              <FolderIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {folder.name}
                </Typography>
                {((folder.total_files_count || folder.files_count) > 0 || folder.subfolders?.length > 0) && (
                  <Typography variant="caption" color="text.secondary">
                    {(folder.total_files_count || folder.files_count) > 0 && `${folder.total_files_count || folder.files_count} file`}
                    {(folder.total_files_count || folder.files_count) > 0 && folder.subfolders?.length > 0 && ' · '}
                    {folder.subfolders?.length > 0 && `${folder.subfolders.length} cartelle`}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default function FileContainer({
  folderId,
  search,
  viewMode,
  ordering,
  onOrderingChange,
  onUploadClick,
  folderTree,
  onFolderSelect,
}) {
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useFiles({ folderId, search, ordering });

  const deleteFile = useDeleteFile();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [moveFile, setMoveFile] = useState(null);
  const [moveLoading, setMoveLoading] = useState(false);
  const [fileContextMenu, setFileContextMenu] = useState(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectionMode = selectedIds.size > 0;
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState('');

  // Rename state
  const [renameFile, setRenameFile] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);

  // Preview state
  const [previewFile, setPreviewFile] = useState(null);

  // Detail panel state
  const [detailFile, setDetailFile] = useState(null);

  // Export links state
  const [exportLinksParams, setExportLinksParams] = useState(null);

  const files = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.results || []);
  }, [data]);

  const handleToggleSelect = useCallback((fileId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const handleSelectionChange = useCallback((newSet) => {
    setSelectedIds(newSet);
  }, []);

  const handleDeselectAll = () => setSelectedIds(new Set());

  const handleSelectAll = () => {
    setSelectedIds(new Set(files.map((f) => f.id)));
  };

  const handleBulkDelete = async () => {
    setBulkLoading(true);
    setBulkError('');
    try {
      await filesAPI.bulkDelete(Array.from(selectedIds));
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      setSelectedIds(new Set());
    } catch (err) {
      setBulkError(err.response?.data?.error || 'Errore durante l\'eliminazione');
    } finally {
      setBulkLoading(false);
      setConfirmBulkDelete(false);
    }
  };

  const handleBulkMoveConfirm = async (_ignored, targetFolderId) => {
    setBulkLoading(true);
    setBulkError('');
    try {
      await filesAPI.bulkMove(Array.from(selectedIds), targetFolderId);
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      setSelectedIds(new Set());
      setBulkMoveOpen(false);
    } catch (err) {
      setBulkError(err.response?.data?.error || 'Errore durante lo spostamento');
      throw err;
    } finally {
      setBulkLoading(false);
    }
  };

  // Find subfolders of current folder from tree
  const subfolders = useMemo(() => {
    if (!folderTree) return [];
    if (!folderId) {
      // Root: show top-level folders
      return folderTree;
    }
    // Find the current folder in tree and return its subfolders
    function findNode(nodes) {
      for (const node of nodes) {
        if (node.id === folderId) return node.subfolders || [];
        if (node.subfolders?.length) {
          const found = findNode(node.subfolders);
          if (found) return found;
        }
      }
      return null;
    }
    return findNode(folderTree) || [];
  }, [folderTree, folderId]);

  const handleQRAction = useCallback(async (file) => {
    try {
      // Marca come scaricato (blocca cancellazione)
      if (!file.qr_downloaded) {
        await filesAPI.markQrDownloaded(file.id);
        queryClient.invalidateQueries({ queryKey: ['files'] });
      }
      // Scarica il QR PNG silenziosamente via axios (con JWT + CORS)
      const res = await api.get(`/files/${file.id}/qrcode/`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr_${file.original_filename}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      // silent
    }
  }, [queryClient]);

  const handleCopyLink = async (file) => {
    const url = file.qr_url || file.qr_direct_url;
    if (url) {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await filesAPI.download(file.id);
      window.open(response.data.download_url, '_blank');
    } catch {
      // silent
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      await deleteFile.mutateAsync(confirmDelete.id);
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleMoveConfirm = async (file, targetFolderId) => {
    setMoveLoading(true);
    try {
      await filesAPI.move(file.id, targetFolderId);
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
    } finally {
      setMoveLoading(false);
    }
  };

  // --- Rename ---
  const handleRenameConfirm = async () => {
    if (!renameFile) return;
    setRenameLoading(true);
    try {
      await filesAPI.rename(renameFile.id, renameName);
      queryClient.invalidateQueries({ queryKey: ['files'] });
      setRenameFile(null);
    } catch {
      // silent - error handling could be added here
    } finally {
      setRenameLoading(false);
    }
  };

  // --- File context menu ---
  const handleFileContextMenu = (event, file) => {
    setFileContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      file,
    });
  };

  const handleCloseFileContextMenu = () => {
    setFileContextMenu(null);
  };

  const handleCtxQR = () => {
    const file = fileContextMenu?.file;
    handleCloseFileContextMenu();
    if (file) handleQRAction(file);
  };

  const handleCtxCopy = () => {
    const file = fileContextMenu?.file;
    handleCloseFileContextMenu();
    if (file) handleCopyLink(file);
  };

  const handleCtxDownload = () => {
    const file = fileContextMenu?.file;
    handleCloseFileContextMenu();
    if (file) handleDownload(file);
  };

  const handleCtxRename = () => {
    const file = fileContextMenu?.file;
    handleCloseFileContextMenu();
    if (file) {
      setRenameFile(file);
      setRenameName(file.original_filename);
    }
  };

  const handleCtxMove = () => {
    const file = fileContextMenu?.file;
    handleCloseFileContextMenu();
    if (file) setMoveFile(file);
  };

  const handleCtxDelete = () => {
    const file = fileContextMenu?.file;
    handleCloseFileContextMenu();
    if (file) setConfirmDelete(file);
  };

  const handleCtxDetail = () => {
    const file = fileContextMenu?.file;
    handleCloseFileContextMenu();
    if (file) setDetailFile(file);
  };

  const sharedProps = {
    files,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    onQRClick: handleQRAction,
    onDelete: (file) => setConfirmDelete(file),
    onDownload: handleDownload,
    onCopyLink: handleCopyLink,
    onContextMenu: handleFileContextMenu,
    selectedIds,
    selectionMode,
    onToggleSelect: handleToggleSelect,
    onSelectionChange: handleSelectionChange,
    search,
    onFileClick: (file) => setPreviewFile(file),
  };

  if (!isLoading && files.length === 0 && subfolders.length === 0) {
    return <EmptyState search={search} onUploadClick={onUploadClick} />;
  }

  return (
    <Box>
      {!search && (
        <FolderCards subfolders={subfolders} onFolderSelect={onFolderSelect} />
      )}

      {/* Bulk selection toolbar */}
      {selectionMode && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            border: 1,
            borderColor: 'primary.light',
            borderRadius: 2,
            bgcolor: 'primary.50',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Chip
            label={`${selectedIds.size} selezionati`}
            color="primary"
            size="small"
            fontWeight={600}
          />

          <Tooltip title="Seleziona tutti">
            <Button
              size="small"
              startIcon={<LibraryAddCheck fontSize="small" />}
              onClick={handleSelectAll}
              sx={{ textTransform: 'none', boxShadow: 'none' }}
            >
              Tutti ({files.length})
            </Button>
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          {bulkError && (
            <Alert severity="error" onClose={() => setBulkError('')} sx={{ py: 0, flex: '1 1 100%' }}>
              {bulkError}
            </Alert>
          )}

          <Tooltip title="Esporta link dei file selezionati">
            <Button
              size="small"
              variant="outlined"
              startIcon={<LinkIcon fontSize="small" />}
              onClick={() => setExportLinksParams({ ids: Array.from(selectedIds) })}
              disabled={bulkLoading}
              sx={{ textTransform: 'none', boxShadow: 'none' }}
            >
              Esporta link
            </Button>
          </Tooltip>

          <Tooltip title="Sposta selezionati">
            <Button
              size="small"
              variant="outlined"
              startIcon={bulkLoading ? <CircularProgress size={14} /> : <DriveFileMoveOutlined fontSize="small" />}
              onClick={() => setBulkMoveOpen(true)}
              disabled={bulkLoading}
              sx={{ textTransform: 'none', boxShadow: 'none' }}
            >
              Sposta
            </Button>
          </Tooltip>

          <Tooltip title="Elimina selezionati">
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={bulkLoading ? <CircularProgress size={14} /> : <DeleteSweep fontSize="small" />}
              onClick={() => setConfirmBulkDelete(true)}
              disabled={bulkLoading}
              sx={{ textTransform: 'none', boxShadow: 'none' }}
            >
              Elimina
            </Button>
          </Tooltip>

          <Tooltip title="Deseleziona tutto">
            <Button
              size="small"
              startIcon={<CheckBoxOutlineBlank fontSize="small" />}
              onClick={handleDeselectAll}
              sx={{ textTransform: 'none', boxShadow: 'none' }}
            >
              Deseleziona
            </Button>
          </Tooltip>
        </Paper>
      )}

      {viewMode === 'grid' ? (
        <FileGrid {...sharedProps} />
      ) : (
        <FileTable {...sharedProps} ordering={ordering} onOrderingChange={onOrderingChange} onRowClick={(file) => setPreviewFile(file)} />
      )}

      {/* File preview dialog */}
      <FilePreviewDialog
        open={Boolean(previewFile)}
        file={previewFile}
        files={files}
        onClose={() => setPreviewFile(null)}
        onDownload={handleDownload}
        onNavigate={(f) => setPreviewFile(f)}
      />

      {/* File context menu */}
      <Menu
        open={fileContextMenu !== null}
        onClose={handleCloseFileContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          fileContextMenu !== null
            ? { top: fileContextMenu.mouseY, left: fileContextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleCtxQR}>
          <ListItemIcon>
            <QrCode2 fontSize="small" />
          </ListItemIcon>
          <ListItemText>{fileContextMenu?.file?.qr_downloaded ? 'Scarica QR Code' : 'Genera e scarica QR Code'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCtxDetail}>
          <ListItemIcon>
            <Info fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dettagli</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCtxCopy}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copia link</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCtxDownload}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCtxRename}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rinomina</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCtxMove}>
          <ListItemIcon>
            <DriveFileMove fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sposta in...</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleCtxDelete}
          disabled={fileContextMenu?.file?.qr_downloaded}
        >
          <ListItemIcon>
            <Delete fontSize="small" color={fileContextMenu?.file?.qr_downloaded ? 'disabled' : 'error'} />
          </ListItemIcon>
          <ListItemText sx={{ color: fileContextMenu?.file?.qr_downloaded ? 'text.disabled' : 'error.main' }}>
            {fileContextMenu?.file?.qr_downloaded ? 'Eliminazione bloccata (QR scaricato)' : 'Elimina'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Move file dialog */}
      <MoveFileDialog
        open={Boolean(moveFile)}
        file={moveFile}
        onClose={() => setMoveFile(null)}
        onConfirm={handleMoveConfirm}
        loading={moveLoading}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Elimina file"
        message={`Sei sicuro di voler eliminare "${confirmDelete?.original_filename}"? L'azione non è reversibile.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
        loading={deleteFile.isPending}
      />

      {/* Bulk delete confirm */}
      <ConfirmDialog
        open={confirmBulkDelete}
        title={`Elimina ${selectedIds.size} file`}
        message={`Sei sicuro di voler eliminare ${selectedIds.size} file selezionati? L'azione non è reversibile.`}
        onConfirm={handleBulkDelete}
        onCancel={() => setConfirmBulkDelete(false)}
        loading={bulkLoading}
      />

      {/* Bulk move dialog */}
      <MoveFileDialog
        open={bulkMoveOpen}
        file={{ original_filename: `${selectedIds.size} file selezionati` }}
        onClose={() => setBulkMoveOpen(false)}
        onConfirm={handleBulkMoveConfirm}
        loading={bulkLoading}
      />

      {/* Rename file dialog */}
      <Dialog
        open={Boolean(renameFile)}
        onClose={() => !renameLoading && setRenameFile(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rinomina file</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome file"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameName.trim() && !renameLoading) {
                handleRenameConfirm();
              }
            }}
            disabled={renameLoading}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRenameFile(null)}
            disabled={renameLoading}
            sx={{ textTransform: 'none' }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleRenameConfirm}
            disabled={!renameName.trim() || renameLoading}
            variant="contained"
            sx={{ textTransform: 'none', boxShadow: 'none' }}
            startIcon={renameLoading ? <CircularProgress size={16} /> : null}
          >
            Rinomina
          </Button>
        </DialogActions>
      </Dialog>

      {/* File detail side panel */}
      <FileDetailPanel
        file={detailFile}
        open={Boolean(detailFile)}
        onClose={() => setDetailFile(null)}
        onQRClick={handleQRAction}
        onDownload={handleDownload}
        onCopyLink={handleCopyLink}
      />

      {/* Export links dialog */}
      <ExportLinksDialog
        open={Boolean(exportLinksParams)}
        onClose={() => setExportLinksParams(null)}
        params={exportLinksParams}
        title={`Esporta link (${selectedIds.size} file selezionati)`}
      />
    </Box>
  );
}
